import "../administration.css";

import {
  ACCESS_HELP,
  BulkBar,
  COLUMNS,
  LeaveGuard,
  RoleCells,
  RoleHeadCells,
  RowTools,
  SaveBar,
  rolesKey,
  toggleIn,
  useRolesDraft,
} from "../../users/AccessGrid";
import { Link, useParams } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import {
  requestFetchContractInfo,
  requestFetchUsers,
} from "../../services/backendRequests";
import { useDispatch, useSelector } from "react-redux";

import { CfAsyncSelect } from "../../components/CfSelect";
import { EmptyState } from "../../components/EmptyState";
import { FilterBar } from "../../clientDashboard/components/FilterBar";
import { HelpTooltip } from "../../analyticsDashboard/components/HelpTooltip/HelpTooltip";
import { LevelHeader } from "../../clientDashboard/components/LevelHeader";
import LoadingImg from "../../components/loading";
import PropTypes from "prop-types";
import _ from "lodash";
import { addDangerAlert } from "../../actions/alerts";
import { updateContractUsers } from "../../actions/company";

const NO_COMPANY = { id: "", name: "No company" };

// ponytail: one page of matches, enough to pick from while typing a name. A "load more"
// in the menu if people ever need to scroll through the whole platform from here.
const SEARCH_LIMIT = 20;
// ponytail: the suggestions shown before anything is typed. A client company rarely holds
// more users with access; the ones beyond it are still found by typing their name.
const SUGGESTION_LIMIT = 50;

// `User#fullname` is the bare lastname when there is no firstname, and lastname is
// nullable
const nameOf = (user) => user.fullname ?? "";

const userLabel = ({ fullname, email, company }) =>
  `${company?.name ?? NO_COMPANY.name} | ${fullname ?? ""}${
    email ? ` (${email})` : ""
  }`;

// Users of one campaign: one row per member, one column per role, the same grid as the
// user page seen from the other side. Membership is the key of `roles`, not a granted
// role: a member holding no role at all still uploads, so it stays a row until it is
// removed.
const ContractUsers = ({ contract, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user);
  const { company } = contract;

  // The server state, and the only reference: the page holds the campaign it fetched,
  // then the one each save answers with, never a cached list it did not read itself.
  const baseline = useMemo(
    () =>
      Object.fromEntries(contract.users.map(({ id, roles }) => [id, roles])),
    [contract.users]
  );

  const { roles, edit, apply, setRoles, reset } = useRolesDraft(baseline);

  // Who the rows are about. The campaign only knows its members, so a user picked in
  // the search box is kept here to have a name to draw.
  const [added, setAdded] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [modifiedOnly, setModifiedOnly] = useState(false);
  const [selected, setSelected] = useState([]);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    if (flash === null) return undefined;

    document
      .querySelector(`[data-flash="${flash}"]`)
      ?.scrollIntoView({ block: "center", behavior: "smooth" });

    const timer = setTimeout(() => setFlash(null), 1200);
    return () => clearTimeout(timer);
  }, [flash]);

  const people = useMemo(
    () => ({
      ...added,
      ...Object.fromEntries(contract.users.map((user) => [user.id, user])),
    }),
    [added, contract.users]
  );

  const remove = (userIds) => {
    edit((next) => {
      userIds.forEach((userId) => delete next[userId]);
      return next;
    });
    setSelected(selected.filter((userId) => !userIds.includes(userId)));
  };

  const addUser = (user) => {
    if (!user) return;
    setAdded((prevState) => ({ ...prevState, [user.id]: user }));
    // A member removed in the draft then picked again gets its saved roles back, like
    // its Undo button
    edit((next) => ({ ...next, [user.id]: baseline[user.id] ?? [] }));
    // The search would hide the row it just added
    setSearch("");
    setFlash(user.id);
  };

  // A member added then removed again is no change, and neither is a role granted then
  // taken back: the draft is compared to the server, not to the clicks.
  const changed = [
    ...new Set([...Object.keys(baseline), ...Object.keys(roles)]),
  ]
    .map(Number)
    .filter(
      (userId) =>
        userId in baseline !== userId in roles ||
        rolesKey(baseline[userId]) !== rolesKey(roles[userId])
    );

  const onlyModified = modifiedOnly && changed.length > 0;

  useEffect(() => {
    if (changed.length === 0) setModifiedOnly(false);
  }, [changed.length]);

  const save = async () => {
    setSaving(true);
    try {
      // The PUT replaces the whole list, so every member is sent, the ones hidden by the
      // filters included, or they would lose the campaign.
      const sent = Object.keys(roles).map(Number);
      const updated = await dispatch(
        updateContractUsers(
          sent.map((userId) => ({ id: userId, roles: roles[userId] })),
          contract.id,
          company
        )
      );
      // The draft stays: it is what the server now holds, so nothing reads as changed,
      // and a click made while the PUT was out is still counted. A member the server
      // dropped (a user deleted meanwhile) leaves the draft too, or it would stay a
      // change with no user to draw.
      const kept = new Set(updated.users.map(({ id }) => id));
      edit((next) => {
        sent
          .filter((userId) => !kept.has(userId))
          .forEach((userId) => delete next[userId]);
        return next;
      });
      onSaved(updated);
      setSelected([]);
    } catch (error) {
      dispatch(addDangerAlert(error));
    }
    setSaving(false);
  };

  // Matches are offered with access only. Before anything is typed, the list suggests
  // the users of the campaign's company; typing searches the whole platform, since the
  // backoffice also puts its own people on client campaigns.
  // Debounced so a name typed is one search, not one per key. react-select swallows a
  // rejected load into "no options", so the error is raised here.
  const loadUsers = useMemo(() => {
    const debounced = _.debounce(
      (q, callback) =>
        requestFetchUsers(currentUser, {
          perPage: q ? SEARCH_LIMIT : SUGGESTION_LIMIT,
          q,
          filters: q
            ? { access: "true" }
            : { access: "true", company_id: company.id },
        })
          .then(({ users }) => callback(users))
          .catch((error) => {
            dispatch(addDangerAlert(error));
            callback([]);
          }),
      300
    );
    // Not returned: debounce hands back the previous call's promise, which react-select
    // would resolve with the previous query's users
    return (q, callback) => {
      debounced(q, callback);
    };
  }, [currentUser, company.id, dispatch]);

  const needle = search.trim().toLowerCase();

  // Removed members stay listed until the save, so the change can be seen and undone
  // from its row, the same way a role taken away stays a grey cross.
  const rows = [...new Set([...Object.keys(baseline), ...Object.keys(roles)])]
    .map(Number)
    .map((userId) => people[userId])
    .filter(
      (user) =>
        (!needle ||
          nameOf(user).toLowerCase().includes(needle) ||
          (user.company?.name ?? "").toLowerCase().includes(needle)) &&
        (!onlyModified || changed.includes(user.id))
    );

  const groups = [
    ...rows
      .reduce((byCompany, user) => {
        const groupCompany = user.company ?? NO_COMPANY;
        const group = byCompany.get(groupCompany.id) ?? {
          company: groupCompany,
          users: [],
        };
        group.users.push(user);
        return byCompany.set(groupCompany.id, group);
      }, new Map())
      .values(),
  ]
    .sort((a, b) => a.company.name.localeCompare(b.company.name))
    .map((group) => ({
      ...group,
      users: group.users.sort((a, b) => nameOf(a).localeCompare(nameOf(b))),
    }));

  // A removed row only offers its Undo: selected, a bulk preset would put it back
  // without a word.
  const visibleIds = rows.filter(({ id }) => id in roles).map(({ id }) => id);

  // A user that left the list keeps no hold on the bulk bar.
  const selectedVisible = selected.filter((userId) =>
    visibleIds.includes(userId)
  );

  const allVisibleSelected =
    visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const selectAll = (ids, allSelected) =>
    setSelected(
      allSelected
        ? selected.filter((userId) => !ids.includes(userId))
        : [...new Set([...selected, ...ids])]
    );

  const renderGroup = ({ company: groupCompany, users }) => {
    const ids = users.filter(({ id }) => id in roles).map(({ id }) => id);
    const allSelected =
      ids.length > 0 && ids.every((userId) => selected.includes(userId));

    return (
      <React.Fragment key={groupCompany.id}>
        <tr className="user-access__grouprow">
          <td className="user-access__check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={allSelected}
              disabled={ids.length === 0}
              title={`Select the users of ${groupCompany.name}`}
              onChange={() => selectAll(ids, allSelected)}
            />
          </td>
          <td colSpan={COLUMNS - 1}>
            <span className="user-access__groupname">{groupCompany.name}</span>
            <span className="user-access__groupcount">{users.length}</span>
          </td>
        </tr>

        {users.map((user) => {
          const member = user.id in roles;
          const list = roles[user.id] ?? [];

          return (
            <tr
              key={user.id}
              className={
                [
                  flash === user.id && "is-new",
                  user.id === currentUser.self.id && "is-self",
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
              data-flash={user.id}
            >
              <td className="user-access__check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selected.includes(user.id)}
                  disabled={!member}
                  aria-label={`Select ${user.fullname}`}
                  onChange={() => setSelected(toggleIn(selected, user.id))}
                />
              </td>

              <td className="user-access__campaign">
                <Link
                  to={`/users/${user.id}`}
                  className="user-access__open"
                  title={`Open ${user.fullname}`}
                  aria-label={`Open ${user.fullname}`}
                >
                  <i className="fa-solid fa-user" />
                </Link>
                {user.fullname}
                {user.id === currentUser.self.id && (
                  <span className="cf-badge cf-badge--ok ms-2">You</span>
                )}
                {!member && (
                  <span className="user-access__inactive">removed</span>
                )}
                {member && !(user.id in baseline) && (
                  <span className="user-access__inactive">new</span>
                )}
              </td>

              {member ? (
                <>
                  <RowTools
                    name={user.fullname}
                    onPreset={(roleValues) => setRoles([user.id], roleValues)}
                    unassign={{
                      label: "Remove",
                      title: `Remove ${user.fullname} from this campaign`,
                      onClick: () => remove([user.id]),
                    }}
                  />
                  <RoleCells
                    list={list}
                    name={user.fullname}
                    onToggle={(role, granted) =>
                      apply([user.id], [role], granted)
                    }
                  />
                </>
              ) : (
                <td className="user-access__rowtools" colSpan={COLUMNS - 2}>
                  <button
                    type="button"
                    title={`Keep ${user.fullname} on this campaign`}
                    onClick={() => setRoles([user.id], baseline[user.id] ?? [])}
                  >
                    Undo
                  </button>
                </td>
              )}
            </tr>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <>
      <LeaveGuard when={changed.length > 0} />

      <FilterBar
        query={search}
        onQuery={setSearch}
        placeholder="Search a member, a company..."
        extraActive={modifiedOnly}
        extra={
          changed.length > 0 && (
            <label
              className={"cf-checkfilter" + (modifiedOnly ? " is-active" : "")}
            >
              <input
                type="checkbox"
                checked={modifiedOnly}
                onChange={(event) => setModifiedOnly(event.target.checked)}
              />
              Modified only
            </label>
          )
        }
        trailing={
          <>
            <CfAsyncSelect
              className="cf-rs contract-users__add"
              classNamePrefix="cf-rs"
              loadOptions={loadUsers}
              defaultOptions
              cacheOptions
              // Applied to the loaded suggestions too, so a user added a moment ago
              // leaves them without a refetch.
              filterOption={({ data }) => !(data.id in roles)}
              value={null}
              onChange={addUser}
              getOptionValue={(user) => user.id}
              getOptionLabel={userLabel}
              placeholder="Add a user..."
              aria-label="Add a user"
              noOptionsMessage={({ inputValue }) =>
                inputValue
                  ? "No user found"
                  : // Only the latest suggestions are loaded, older users of the
                    // company are still found by name
                    `No suggestion left for ${company.name}, type a name...`
              }
            />
            <HelpTooltip helpTooltip={ACCESS_HELP} placement="bottom-end" />
          </>
        }
      />

      {selectedVisible.length > 0 && (
        <BulkBar
          count={selectedVisible.length}
          noun="user"
          onPreset={(roleValues) => setRoles(selectedVisible, roleValues)}
          onAddRole={(role) => apply(selectedVisible, [role], true)}
          unassign={{
            label: "Remove",
            title: "Remove the selected users from this campaign",
            onClick: () => remove(selectedVisible),
          }}
          onDeselect={() => setSelected([])}
        />
      )}

      <div className="user-access-scroll">
        {rows.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-users"
            title={needle ? "No member" : "No member yet"}
            text={
              needle
                ? "No member matches your search."
                : "Add a user with the search box above to grant the first access."
            }
          />
        ) : (
          <div className="user-access-wrap">
            <table className="user-access">
              <thead>
                <tr>
                  <th className="user-access__check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allVisibleSelected}
                      title="Select every user listed"
                      onChange={() => selectAll(visibleIds, allVisibleSelected)}
                    />
                  </th>
                  <th>User</th>
                  <th />
                  <RoleHeadCells />
                </tr>
              </thead>
              <tbody>{groups.map(renderGroup)}</tbody>
            </table>
          </div>
        )}
      </div>

      {changed.length > 0 && (
        <SaveBar
          count={changed.length}
          noun="user"
          onReview={
            !modifiedOnly &&
            (() => {
              setSearch("");
              setModifiedOnly(true);
            })
          }
          onCancel={() => {
            reset();
            setAdded({});
          }}
          onSave={save}
          saving={saving}
        />
      )}
    </>
  );
};

// The page around the grid. It reads its campaign on every arrival rather than from the
// company page's list: the PUT replaces the whole member list, so a stale copy would
// silently restore an access removed elsewhere in the meantime.
const ContractUsersPage = () => {
  const { companyId, contractId } = useParams();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user);
  const [contract, setContract] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    requestFetchContractInfo({ id: contractId }, currentUser)
      .then(setContract)
      .catch((error) => {
        dispatch(addDangerAlert(error));
        setFailed(true);
      });
    // Once per campaign: the saves answer with the updated one
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  // A campaign of another company is not this address
  const missing = failed || (contract && contract.company?.id !== companyId);
  const company = !missing && contract?.company;
  const companyPath = `/administration/company/${companyId}`;

  return (
    <div className="page-content container-fluid cf-page">
      <LevelHeader
        path={[
          { label: "Administration", to: "/administration" },
          { label: company?.name ?? "Company", to: companyPath },
          {
            label: company ? contract.name : "Campaign",
            to: `${companyPath}/contract/${contractId}`,
          },
          { label: "Users" },
        ]}
        metrics={
          company && [{ value: contract.users.length, label: "Members" }]
        }
      />

      {missing ? (
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Campaign not available"
          text="This campaign could not be loaded. Check the address, or try again."
        />
      ) : company ? (
        <ContractUsers contract={contract} onSaved={setContract} />
      ) : (
        <LoadingImg visible />
      )}
    </div>
  );
};

export default ContractUsersPage;

ContractUsers.propTypes = {
  contract: PropTypes.shape({
    id: PropTypes.string.isRequired,
    company: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        fullname: PropTypes.string,
        roles: PropTypes.arrayOf(PropTypes.string).isRequired,
      })
    ).isRequired,
  }).isRequired,
  onSaved: PropTypes.func.isRequired,
};
